import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  ConfirmationDialog,
  OptionsButton,
  TomTable,
  TomTableActionsProps,
  tomTableColumnFilterPresets,
  useTomTable,
} from '@tom-ui/ui'
import { appStore, useTablePreferencesConfig } from '@tom-ui/utils'
import { PierResponse } from '@yard/app/api'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { PierStore } from '../../../../stores/pier.store'
import { usePierMessageHandler } from '../../hooks/usePierMessageHandler'

interface Props {
  store: PierStore
}

export const PierTable = observer(({ store }: Props) => {
  const { t } = useTranslate()
  const navigate = useNavigate()
  const [deleteItems, setDeleteItems] = useState<{ id: string; name: string }[]>([])
  const { getDeleteSuccessMessage, getExceptionMessage } = usePierMessageHandler()

  const handleDelete = async () => {
    if (!deleteItems.length) return
    try {
      const requests = deleteItems.map(item => store.deletePier(item.id))

      await Promise.all(requests)
      appStore.setShowAlert('success', getDeleteSuccessMessage())
      setDeleteItems([])
    } catch (error) {
      appStore.setShowAlert('error', getExceptionMessage(error))
    }
  }

  const columns = useMemo<MRT_ColumnDef<PierResponse>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
    ],
    [t],
  )

  const tableActions = useMemo<TomTableActionsProps<PierResponse>[]>(
    () => [
      {
        label: t('delete', 'Delete'),
        onClick: rows => {
          const itemsToDelete = rows.map(row => ({
            id: row.original.id,
            name: row.original.name,
          }))
          setDeleteItems(itemsToDelete)
        },
      },
    ],
    [t],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig('yard/piers')

  const table = useTomTable<PierResponse>(
    {
      columns,
      data: store.items,
      enableExpanding: false,
      layoutMode: 'grid',
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => navigate(`${row.original.id}`),
            testId: `edit-${row.original.id}`,
          },
          {
            label: t('delete', 'Delete'),
            onClick: () => setDeleteItems([{ id: row.original.id, name: row.original.name }]),
            testId: `delete-${row.original.id}`,
          },
        ]
        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='pier-options-btn'
          />
        )
      },
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 202px)',
          minHeight: 'calc(100vh - 202px)',
        },
      },
    },
    tableActions,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <>
      <TomTable table={table} />
      <ConfirmationDialog
        open={deleteItems.length > 0}
        title={
          deleteItems.length === 1
            ? t('deletePier', 'Delete pier?')
            : t('deletePiers', 'Delete piers?')
        }
        message={
          deleteItems.length === 1
            ? t('deletePierMessage', 'Pier {pier} will be deleted.', {
                pier: deleteItems[0].name,
              })
            : t('deletePiersMessage', 'Piers {piers} will be deleted.', {
                piers: deleteItems.map(item => item.name).join(', '),
              })
        }
        primaryActionText={t('yesDelete', 'Yes, delete')}
        closeLabel={t('noCancel', 'No, cancel')}
        maxWidth='xs'
        onConfirm={() => handleDelete()}
        onClose={() => setDeleteItems([])}
      />
    </>
  )
})
